import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { SharedModule } from '../shared';

interface Seccion {
  nombre: string;
  url: string;
  /** Trazos del icono (viewBox 24, sin relleno). */
  icono: string;
  /** Lleva la flecha de desplegable del diseño. De momento enlaza a la página de la sección. */
  flecha?: boolean;
  /** Campañas en negrita (Black Friday, 11.11). */
  destacada?: boolean;
}

/**
 * Segunda fila de la cabecera de escritorio (diseño V26): las secciones del sitio con su
 * icono fino. Sólo enlaces reales: «Gratis» es la etiqueta /cosas-gratis/ y «Promociones»
 * la etiqueta /promociones/ (las dos ya salían en «Explorar categorías»); Black Friday y
 * 11.11 son las campañas /blackfriday/ y /11-11/ del enrutador.
 * Spec: odd/tasks/tiendas-voz-cabecera.md, S5.
 */
@Component({
  selector: 'app-cabecera-secciones',
  standalone: true,
  imports: [SharedModule],
  template: `
    <nav aria-label="Secciones" class="secciones">
      @for (s of secciones; track s.url) {
        <a class="lk" [class.destacada]="s.destacada" [href]="s.url" appGoToUrl
           [attr.aria-current]="url() === s.url ? 'page' : null">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" [attr.stroke-width]="s.destacada ? 2 : 1.9"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path [attr.d]="s.icono"/></svg>{{ s.nombre }}@if (s.flecha) {<svg class="flecha" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>}
        </a>
      }
    </nav>
  `,
  styles: [`
    :host { display: block; }
    .secciones { height: 44px; display: flex; align-items: center; gap: 24px; overflow: hidden; }
    .lk {
      display: inline-flex; align-items: center; gap: 6px; height: 44px; padding: 0 2px;
      font: 600 14px/1 var(--mc-font-title); color: var(--c26-texto-2, #2E2A33);
      white-space: nowrap; text-decoration: none;
    }
    .lk:hover, .lk[aria-current='page'] { color: #B3141A; }
    .lk:focus-visible { outline: 2px solid #C81A20; outline-offset: 2px; border-radius: 4px; }
    .destacada { font-weight: 800; color: var(--c26-texto, #17151A); }
    .flecha { margin-left: -2px; }
    /* Móvil (odd/tasks/movil-cabecera-tarjeta-cuenta.md, S3): la misma fila, deslizable. */
    :host(.movil) .secciones {
      gap: 20px; padding: 0 16px; overflow-x: auto; overflow-y: hidden;
      scrollbar-width: none; -webkit-overflow-scrolling: touch;
    }
    :host(.movil) .secciones::-webkit-scrollbar { display: none; }
    :host(.movil) .lk { flex-shrink: 0; font-size: 14px; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.movil]': 'movil()' },
})
export class CabeceraSeccionesComponent {
  /** Ruta actual, para marcar la sección en la que se está. */
  readonly url = input<string>('');

  /** Versión de móvil: la fila se desliza en horizontal en vez de recortarse. */
  readonly movil = input<boolean>(false);

  readonly secciones: Seccion[] = [
    { nombre: 'Categorías', url: '/categorias/', flecha: true,
      icono: 'M4.5 3h4A1.5 1.5 0 0 1 10 4.5v4A1.5 1.5 0 0 1 8.5 10h-4A1.5 1.5 0 0 1 3 8.5v-4A1.5 1.5 0 0 1 4.5 3zM15.5 3h4A1.5 1.5 0 0 1 21 4.5v4a1.5 1.5 0 0 1-1.5 1.5h-4A1.5 1.5 0 0 1 14 8.5v-4A1.5 1.5 0 0 1 15.5 3zM4.5 14h4a1.5 1.5 0 0 1 1.5 1.5v4A1.5 1.5 0 0 1 8.5 21h-4A1.5 1.5 0 0 1 3 19.5v-4A1.5 1.5 0 0 1 4.5 14zM15.5 14h4a1.5 1.5 0 0 1 1.5 1.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a1.5 1.5 0 0 1-1.5-1.5v-4a1.5 1.5 0 0 1 1.5-1.5z' },
    { nombre: 'Cupones', url: '/cupones/', flecha: true,
      icono: 'M3 8a2 2 0 0 0 0 4v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4a2 2 0 0 0 0-4V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2zM14 4v14' },
    { nombre: 'Gratis', url: '/cosas-gratis/', flecha: true,
      icono: 'M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7z' },
    { nombre: 'Tiendas', url: '/tiendas/', icono: 'M3 9h18l-1.5-5h-15zM4 9v11h16V9M9 20v-6h6v6' },
    { nombre: 'Promociones', url: '/promociones/', icono: 'M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1zM15 9a3 3 0 0 1 0 6' },
    // 11.11 antes que Black Friday: va antes en el calendario (31/10/2026).
    { nombre: '11.11 en AliExpress', url: '/11-11/', destacada: true,
      icono: 'M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM3 9h18M8 2v4M16 2v4' },
    { nombre: 'Black Friday', url: '/blackfriday/', destacada: true,
      icono: 'M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8zM7.5 6a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z' },
  ];
}
